import { inject, Injectable } from '@angular/core';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { Configuration } from 'src/app/models/configurators-2d/applications/kitchen/configuration';
import { BaseAppService } from '../base-app-service';
import { BathroomHbmConfigurationData } from '../../../stores/tunnel/model/configuration-data/bathroom-hbm-configuration-data';
import { TunnelDataStore } from '../../../stores/tunnel/tunnel-data.store';
import { Subscription } from 'rxjs';
import { BathroomHbmConfiguration } from 'src/app/models/configurators-2d/tunnel/bathroom-hbm/bathroom-hbm-configuration';
import { BaseBathroomHbmConfigurationDto } from 'src/app/models/configurators-2d/tunnel/bathroom-hbm/base-bathroom-hbm-configuration-dto';
import { BathroomHbmCreateUpdateTunnel } from 'src/app/models/configurators-2d/tunnel/bathroom-hbm/bathroom-hbm-create-update-tunnel';
import { BathroomHbmInstallationDto } from 'src/app/models/configurators-2d/tunnel/bathroom-hbm/bathroom-hbm-installation-dto';
import { BathroomHbmTunnelPricingDto } from 'src/app/models/configurators-2d/tunnel/bathroom-hbm/bathroom-hbm-tunnel-pricing-dto';
import { BathroomHbmTunnelFinancingDto } from 'src/app/models/configurators-2d/tunnel/bathroom-hbm/bathroom-hbm-tunnel-financing-dto';
import {
  ApplyDiscountInput,
  ComputeInstallationOptions,
  FundingLegalInformation,
  GetFundingDataInput,
  GetFundingLegalInformationInput,
  GetInstallationDataInput,
  GetPricingInput,
  TunnelUpdateInstallation,
  UpdateInstallationInput,
} from 'src/app/models/configurators-2d/tunnel/common-tunnel-dtos';
import { Funding, FundingState, InstallationState, Price, PricingState } from 'src/app/stores/tunnel/model/tunnel-data-state';
import { DiscountTypeEnum } from 'src/app/enum/discount-type.enum';
import { HbmPdfViewModel } from 'src/app/models/configurators-2d/tunnel/common-hbm-tunnel-dtos';
import { logError } from '../../helpers/sentry.helper';
import { FeatureSettingsService } from '../../feature-settings/feature-settings.service';
import { BomDataHelper } from '../../helpers/bom-data-helper';
import { FundingRehydrationService } from '../funding-rehydration.service';
import { GetCustomerFidelityInfoNouveauPointInput } from '../../fidelity/fidelity';
import { FidelityService } from '../../fidelity/fidelity.service';
import { Customer } from '../../kingfisher/customer';
import { environment } from 'src/environments/environment';

/**
 * Service du parcours de vente SDB ByMe/HBM 3D.
 *
 * Dialogue avec le back `API/BathroomHbm/Tunnel` (schéma `bathroom_hbm`), qui calcule les
 * prestations de pose via SPID à partir de la BOM HomeByMe.
 * À distinguer de `BathroomService` (Kentia 2D, `API/Bathroom`), qui n'a pas de pose.
 */
@Injectable({
  providedIn: 'root',
})
export class BathroomHbmService extends BaseAppService<Configuration> {
  private readonly fundingRehydration = inject(FundingRehydrationService);
  private currentConfigurationData: BathroomHbmConfigurationData;
  private currentInstallation: InstallationState;
  private currentPricing: PricingState;
  private currentFunding: FundingState;
  private currentCustomer: Customer;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly featureSettingsService: FeatureSettingsService,
    private readonly tunnelDataStore: TunnelDataStore,
    private readonly fidelityService: FidelityService
  ) {
    super(CONFIG_CONTEXT.BATHROOM, Configuration);
    this.subscribeToObservable();
  }

  /** Kill-switch d'environnement sur la pose SDB HBM, symétrique de Kentia et Menuiserie. */
  isInstallationDisabled(): boolean {
    return environment.installationSpecifics.bathroomHbm.disableInstallation;
  }

  /**
   * Crée (ou met à jour) le tunnel SDB HBM à partir de la BOM renvoyée par l'iframe ByMe.
   */
  public async createBathroomHbm(initialData: BathroomHbmConfigurationData): Promise<void> {
    const currentConfigurationId = this.currentConfigurationData?.configurationId;

    let resultCreate: BathroomHbmConfiguration = null;
    if (currentConfigurationId) {
      await this.updateBathroomHbmTunnel({
        bom: initialData.bom,
        uuidhbm: initialData.HBMConfigurationId,
        title: initialData.projectTitle,
        tunnelConfigurationId: currentConfigurationId,
      } as BathroomHbmCreateUpdateTunnel);
    } else {
      resultCreate = await this.createBathroomHbmTunnel({
        bom: initialData.bom,
        uuidhbm: initialData.HBMConfigurationId,
        title: initialData.projectTitle,
      } as BathroomHbmCreateUpdateTunnel);
    }

    this.tunnelDataStore.resetCurrentInstallation();
    this.tunnelDataStore.resetCurrentPricing();
    this.tunnelDataStore.resetCurrentFunding();
    this.tunnelDataStore.updateCurrentDiscount(DiscountTypeEnum.Discount0);

    this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.BATHROOM, {
      ...initialData,
      configurationId: currentConfigurationId ?? resultCreate?.id,
      bathroomHbmConfigurationId:
        this.currentConfigurationData?.bathroomHbmConfigurationId ?? resultCreate?.bathroomHbmConfigurationId,
    } as BathroomHbmConfigurationData);
  }

  /**
   * Persiste la BOM éditée dans la liste d'achat (quantités modifiées) afin que la pose
   * soit recalculée sur la BOM à jour. Les champs manquants sont complétés à partir de la
   * configuration courante ; la création/mise à jour est déléguée à {@link createBathroomHbm}.
   */
  public async updateBathroomHbmConfiguration(data: BathroomHbmConfigurationData): Promise<void> {
    const merged = { ...this.currentConfigurationData, ...data } as BathroomHbmConfigurationData;
    // Repli sur le projet HBM courant si l'UUID/titre n'ont pas été propagés.
    merged.HBMConfigurationId = merged.HBMConfigurationId ?? this.current.HBMProject?.uuid;
    merged.projectTitle = merged.projectTitle ?? this.current.HBMProject?.name;

    await this.createBathroomHbm(merged);
  }

  /**
   * Force la sauvegarde du tunnel courant (BOM + titre) en base, sans réinitialiser l'état
   * de pose. Déclenché par le bouton « Enregistrer » de l'action-bar.
   */
  public async save(): Promise<void> {
    const configurationId = this.currentConfigurationData?.configurationId;
    if (!configurationId) {
      return;
    }

    await this.updateBathroomHbmTunnel({
      tunnelConfigurationId: configurationId,
      bom: this.currentConfigurationData.bom,
      uuidhbm: this.currentConfigurationData.HBMConfigurationId ?? this.current.HBMProject?.uuid,
      title: this.currentConfigurationData.projectTitle ?? this.current.HBMProject?.name,
    } as BathroomHbmCreateUpdateTunnel);
  }

  /**
   * Récupère l'estimation des prestations de pose (SPID) pour le projet courant.
   *
   * `options.usePersistedHouseAge` doit être posé au chargement d'un tunnel existant : voir
   * {@link ComputeInstallationOptions}.
   */
  public async computeInstallation(options: ComputeInstallationOptions = {}): Promise<void> {
    if (!(await this.featureSettingsService.isPoseEnabled())) {
      this.tunnelDataStore.disableCurrentInstallation();
      return;
    }

    try {
      this.tunnelDataStore.setInstallationLoading(true);

      const result = await this.getInstallationData({
        tunnelConfigurationId: this.currentConfigurationData.configurationId,
        houseMoreThan2YearsOld: options.usePersistedHouseAge ? undefined : this.currentInstallation?.more2Years,
      });

      this.tunnelDataStore.resetCurrentPricing();
      this.tunnelDataStore.resetCurrentFunding();

      this.tunnelDataStore.updateCurrentInstallation({
        more2Years: result.houseMoreThan2YearsOld,
        withInstallation: result.withInstallation,
        detailInstallation: JSON.parse(result.installationPrice?.installationDetails ?? 'null'),
      });

      this.loadPricingFromInstallation(result);
    } catch (error) {
      logError(error);
    } finally {
      this.tunnelDataStore.setInstallationLoading(false);
    }
  }

  /**
   * Alimente le pricing courant (conception, pose, livraison, delta) à partir de la réponse d'installation.
   */
  private loadPricingFromInstallation(input: BathroomHbmInstallationDto): void {
    const conceptionDetail = input.withInstallation
      ? input.conceptionPrices?.withInstallation
      : input.conceptionPrices?.withoutInstallation;

    this.tunnelDataStore.updateCurrentPricing({
      totalConception: this.toPrice(conceptionDetail?.totalPrice),
      delivery: this.toPrice(input.deliveryPrice),
      totalInstallation: this.toPrice(input.installationPrice?.price),
      totalWithoutFunding: this.toPrice(conceptionDetail?.totalPrice),
      detailConception: this.getDetailConception(input),
      delta: this.toPrice(input.conceptionPrices?.delta),
    });
  }

  /**
   * Détail de conception affiché sous le total : la ligne « produits » avec pose,
   * sinon la ventilation par catégorie sans pose.
   */
  private getDetailConception(input: BathroomHbmInstallationDto): Map<string, Price> {
    if (input.withInstallation) {
      return new Map<string, Price>([['productsPrice', this.toPrice(input.conceptionPrices?.withInstallation?.productsPrice)]]);
    }

    return new Map<string, Price>(
      Object.entries(input.conceptionPrices?.categoryTotals ?? {}).map(([key, value]): [string, Price] => [
        key,
        this.toPrice(value),
      ])
    );
  }

  /**
   * Active ou désactive la pose sur le tunnel courant.
   */
  public async updateWithInstallation(withInstallation: boolean): Promise<void> {
    if (!(await this.featureSettingsService.isPoseEnabled())) {
      this.tunnelDataStore.disableCurrentInstallation();
      return;
    }

    await this.updateInstallation({
      tunnelConfigurationId: this.currentConfigurationData.configurationId,
      installationUpdate: { withInstallation } as TunnelUpdateInstallation,
    });
  }

  /**
   * Recharge le tunnel SDB HBM d'un projet existant (reprise depuis la liste de projets),
   * à partir de l'UUID HomeByMe, et réhydrate le store (id, BOM, titre) pour que les étapes
   * Pose/Validation disposent de la configuration persistée.
   *
   * Comme Storage (`loadStorageConfigurationFromHBM` → `loadStoragePricing`), on réhydrate
   * aussi l'état de pose persisté (choix pose, âge d'habitation, détail) : sans ça, l'étape Pose
   * réinitialiserait `WithInstallation` à sa valeur par défaut et écraserait le choix du vendeur.
   * On recharge ensuite le pricing pour restaurer le financement persisté (offre + apport) et la
   * remise ; sinon l'étape Financement repartirait de zéro (apport 0, aucune offre sélectionnée).
   */
  public async loadBathroomHbmConfigurationFromHBM(hbmConfigurationId: string): Promise<void> {
    // GUID en majuscules (format attendu par HomeByMe).
    const uuid = hbmConfigurationId.toUpperCase();

    try {
      const result = await this.getBathroomHbmTunnelByUUID(uuid);

      this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.BATHROOM, {
        configurationId: result?.id,
        bathroomHbmConfigurationId: result?.bathroomHbmConfigurationId,
        HBMConfigurationId: uuid,
        projectTitle: result?.title,
        bom: result?.bom,
      } as BathroomHbmConfigurationData);

      if (result?.id && !BomDataHelper.isBomUnavailableForProductList(result?.bom)) {
        // Réhydrate `currentInstallation` depuis l'état persisté (pose/âge/détail) avant l'étape Pose.
        await this.computeInstallation({ usePersistedHouseAge: true });
        // Réhydrate pricing + financement (offre/apport) + remise persistés avant l'étape Financement.
        await this.loadBathroomHbmPricing(result.id);
        // Filet : `Tunnel/pricing` ne ramène pas toujours les offres persistées.
        await this.fundingRehydration.rehydrateIfNeeded(result.id, this);
      }
    } catch (error) {
      logError(error);
    }
  }

  private subscribeToObservable(): void {
    this.subscriptions.push(
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.currentConfigurationData = configurationData.get(
            CONFIG_CONTEXT.BATHROOM
          ) as BathroomHbmConfigurationData;
        }
      }),
      this.tunnelDataStore.getCurrentInstallation().subscribe(installation => {
        if (installation) {
          this.currentInstallation = installation;
        }
      }),
      this.tunnelDataStore.getCurrentPricing().subscribe(pricing => {
        if (pricing) {
          this.currentPricing = pricing;
        }
      }),
      this.tunnelDataStore.getCurrentFunding().subscribe(funding => {
        if (funding) {
          this.currentFunding = funding;
        }
      }),
      this.tunnelDataStore.getCurrentCustomer().subscribe(customer => {
        if (customer) {
          this.currentCustomer = customer.userData;
        }
      })
    );
  }

  // #region Pricing / Financement / Remise

  /** Charge le pricing courant du tunnel (sans recalcul), utilisé par l'étape Financement. */
  public async loadBathroomHbmPricing(tunnelConfigurationId: string): Promise<void> {
    try {
      await this.computeBathroomHbmPricing({ tunnelConfigurationId, shouldRecalculate: false });
    } catch (error) {
      logError(error);
    }
  }

  /** Applique une remise au projet, recalcule le pricing (et le financement si activé) puis la fidélité. */
  public async updateDiscount(input: ApplyDiscountInput): Promise<void> {
    this.tunnelDataStore.setPricingLoading(true);
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();
    const discountInput: ApplyDiscountInput = {
      ...input,
      requiresFinancingRecalculation: financementEnabled && input.requiresFinancingRecalculation,
    };
    try {
      const result = await this.applyDiscount(discountInput);
      await this.updateBathroomHbmPricingFromTunnelPricing(result);
      await this.recomputeFidelity();
      this.tunnelDataStore.updateCurrentDiscount(discountInput.discountType);
    } catch (error) {
      logError(error);
    } finally {
      this.tunnelDataStore.setPricingLoading(false);
    }
  }

  /** Calcule les offres de financement pour l'apport saisi et met à jour le pricing. */
  public async computeFunding(input: GetFundingDataInput): Promise<void> {
    if (!(await this.featureSettingsService.isFinancementEnabled())) {
      // Financement coupé : on recharge quand même le pricing pour que les totaux suivent
      // le scénario de pose courant (le mapper de pricing repasse par disableCurrentFunding).
      this.tunnelDataStore.disableCurrentFunding();
      await this.loadBathroomHbmPricing(input.tunnelConfigurationId);
      return;
    }

    try {
      this.tunnelDataStore.setFundingLoading(true);
      const result = await this.getFundingData(input);
      await this.updateBathroomHbmPricingFromTunnelPricing(result);
    } catch (error) {
      logError(error);
    } finally {
      this.tunnelDataStore.setFundingLoading(false);
    }
  }

  /** Sélectionne une offre de financement (barème + durée) et recharge le pricing. */
  public async selectOffer(code: string, duration: number): Promise<void> {
    if (!(await this.featureSettingsService.isFinancementEnabled())) {
      this.tunnelDataStore.disableCurrentFunding();
      return;
    }

    await this.getFundingLegalInformation({
      tunnelConfigurationId: this.currentConfigurationData.configurationId,
      selectedBareme: code,
      financingDuration: duration,
    });
    await this.loadBathroomHbmPricing(this.currentConfigurationData.configurationId);
  }

  private async computeBathroomHbmPricing(input: GetPricingInput): Promise<void> {
    try {
      const result = await this.getPricing(input);
      this.tunnelDataStore.setPricingLoading(true);
      await this.updateBathroomHbmPricingFromTunnelPricing(result);
    } catch (error) {
      logError(error);
    } finally {
      this.tunnelDataStore.setPricingLoading(false);
    }
  }

  private async recomputeFidelity(): Promise<void> {
    if (!(await this.fidelityEnabled())) return;
    if (!this.currentPricing?.totalWithoutFunding?.actualAmount || !this.currentCustomer) return;
    await this.computeFidelityPoint({
      amount: this.currentPricing.totalWithoutFunding.actualAmount,
      customerId: this.currentCustomer.kfId,
    });
  }

  /**
   * Alimente pricing, pose, financement et remise à partir d'un {@link BathroomHbmTunnelPricingDto}.
   */
  private async updateBathroomHbmPricingFromTunnelPricing(input: BathroomHbmTunnelPricingDto): Promise<void> {
    const featureSettings = await this.featureSettingsService.getSettings();

    if (featureSettings.pose) {
      this.updateBathroomHbmInstallation(input);
    } else {
      this.tunnelDataStore.disableCurrentInstallation();
    }

    if (featureSettings.pose && this.currentInstallation?.withInstallation && input.conceptionPrices?.withInstallation) {
      this.computeBathroomHbmPricingWithInstallation(input);
    } else if (input.conceptionPrices?.withoutInstallation) {
      this.computeBathroomHbmPricingWithoutInstallation(input);
    }

    if (featureSettings.financement) {
      this.loadBathroomHbmFunding(input);
    } else {
      this.tunnelDataStore.disableCurrentFunding();
    }

    if (typeof input.discountEnum === 'number') {
      this.tunnelDataStore.updateCurrentDiscount(this.discountValueToDiscountEnum(input.discountEnum as number));
    } else {
      this.tunnelDataStore.updateCurrentDiscount(input.discountEnum);
    }
  }

  private computeBathroomHbmPricingWithInstallation(result: BathroomHbmTunnelPricingDto): void {
    this.baseComputeBathroomHbmPricing(result);
    this.tunnelDataStore.updateCurrentPricing({
      detailConception: new Map<string, Price>([
        ['productsPrice', this.toPrice(result.conceptionPrices?.withInstallation?.productsPrice)],
      ]),
      totalConception: this.toPrice(result.conceptionPrices?.withInstallation?.productsPrice),
    });
  }

  private computeBathroomHbmPricingWithoutInstallation(result: BathroomHbmTunnelPricingDto): void {
    this.baseComputeBathroomHbmPricing(result);
    this.tunnelDataStore.updateCurrentPricing({
      detailConception: new Map<string, Price>([
        ['productsPrice', this.toPrice(result.conceptionPrices?.withoutInstallation?.productsPrice)],
      ]),
      totalConception: this.toPrice(result.conceptionPrices?.withoutInstallation?.productsPrice),
    });
  }

  private baseComputeBathroomHbmPricing(result: BathroomHbmTunnelPricingDto): void {
    const installationDisabled = !this.featureSettingsService.poseEnabled;
    const totalWithoutFunding = installationDisabled
      ? result.conceptionPrices?.withoutInstallation?.totalPrice ??
        result.conceptionPrices?.withoutInstallation?.productsPrice ??
        result.totalWithoutFinancing
      : result.totalWithoutFinancing;

    this.tunnelDataStore.updateCurrentPricing({
      delivery: this.toPrice(result.deliveryPrice),
      totalInstallation: installationDisabled ? this.toPrice(0) : this.toPrice(result.installationPrices?.price),
      totalWithoutFunding: this.toPrice(totalWithoutFunding),
      delta: installationDisabled ? this.toPrice(0) : this.toPrice(result.conceptionPrices?.delta),
      totalWithFunding: result.totalWithFinancing,
    });
  }

  private updateBathroomHbmInstallation(result: BathroomHbmTunnelPricingDto): void {
    let detailInstallation = null;
    if (result.installationPrices?.installationDetails != undefined) {
      detailInstallation = JSON.parse(result.installationPrices.installationDetails);
    }

    this.tunnelDataStore.updateCurrentInstallation({
      more2Years: result.houseMoreThan2YearsOld,
      withInstallation: result.withInstallation,
      detailInstallation,
    });
  }

  private loadBathroomHbmFunding(result: BathroomHbmTunnelPricingDto): void {
    this.tunnelDataStore.updateCurrentFunding({
      ...this.mapBathroomHbmTunnelPricingFundingState(result),
    });
  }

  private mapBathroomHbmTunnelPricingFundingState(input: BathroomHbmTunnelPricingDto): FundingState {
    return {
      downPayment: input.downPayment,
      fundingWithInstallation: this.buildFundingFromDto(input.financingWithInstallation),
      fundingWithoutInstallation: this.buildFundingFromDto(input.financing),
    };
  }

  /**
   * Construit un {@link Funding} à partir d'un DTO de financement persisté.
   * Replie sur un funding vide si le scénario est absent — le back renvoie `null` (pas `undefined`)
   * pour le scénario non retenu, d'où le garde sur `offers` plutôt que sur l'objet lui-même.
   */
  private buildFundingFromDto(financing?: BathroomHbmTunnelFinancingDto): Funding {
    if (!financing?.offers) {
      return this.buildEmptyFunding();
    }

    const offerTable = this.mapRawOffersLiteOfferResults(JSON.parse(financing.offers));
    return {
      code: financing.select_bareme,
      duration: financing.select_duration,
      offerTable,
      ...this.getDataFromFundingOfferTable(financing.select_bareme, financing.select_duration, offerTable),
    };
  }

  // #endregion

  // #region Fidélité

  private async fidelityEnabled(): Promise<boolean> {
    return this.featureSettingsService.isFidelityEnabled();
  }

  /**
   * Calcule les points/coupons de fidélité (carte gratuite et C'Casto Max) pour le montant du projet,
   * dans les deux scénarios pose/sans pose, et alimente `currentPricing.fidelity`.
   */
  public async computeFidelityPoint(input: GetCustomerFidelityInfoNouveauPointInput): Promise<void> {
    if (!(await this.fidelityEnabled())) return;
    try {
      this.tunnelDataStore.setFidelityLoading(true);

      const [withInstallation, withoutInstallation] = await Promise.all([
        this.fidelityService.getCustomerFidelityCardInfoNouveauPoint(this.buildFidelityWithInstallationInput(input)),
        this.fidelityService.getCustomerFidelityCardInfoNouveauPoint(this.buildFidelityWithoutInstallationInput(input)),
      ]);

      this.tunnelDataStore.updateCurrentPricing({
        ...this.currentPricing,
        fidelity: [withInstallation, withoutInstallation],
      });
    } catch (error) {
      logError(error);
    } finally {
      this.tunnelDataStore.setFidelityLoading(false);
    }
  }

  private buildFidelityWithInstallationInput(
    input: GetCustomerFidelityInfoNouveauPointInput
  ): GetCustomerFidelityInfoNouveauPointInput {
    const otherTotal =
      this.currentFunding?.fundingWithInstallation?.total > 0
        ? this.currentFunding?.fundingWithInstallation?.total
        : input.amount + (this.currentPricing?.delta?.actualAmount ?? 0);
    return {
      ...input,
      amount: this.currentInstallation?.withInstallation ? Math.floor(input.amount) : Math.floor(otherTotal),
    };
  }

  private buildFidelityWithoutInstallationInput(
    input: GetCustomerFidelityInfoNouveauPointInput
  ): GetCustomerFidelityInfoNouveauPointInput {
    const otherTotal =
      this.currentFunding?.fundingWithoutInstallation?.total > 0
        ? this.currentFunding?.fundingWithoutInstallation?.total
        : input.amount - (this.currentPricing?.delta?.actualAmount ?? 0);
    return {
      ...input,
      amount: !this.currentInstallation?.withInstallation ? Math.floor(input.amount) : Math.floor(otherTotal),
    };
  }

  // #endregion

  // #region Appels API

  private createBathroomHbmTunnel(input: BathroomHbmCreateUpdateTunnel): Promise<BathroomHbmConfiguration> {
    return this.postHbm<BathroomHbmCreateUpdateTunnel, BathroomHbmConfiguration>(
      this.apiUrl + 'Tunnel/create',
      input
    );
  }

  private updateBathroomHbmTunnel(input: BathroomHbmCreateUpdateTunnel): Promise<BathroomHbmConfiguration> {
    return this.putHbm<BathroomHbmCreateUpdateTunnel, BathroomHbmConfiguration>(
      this.apiUrl + 'Tunnel/update/' + input.tunnelConfigurationId,
      input
    );
  }

  private getBathroomHbmTunnelByUUID(hbmUUID: string): Promise<BaseBathroomHbmConfigurationDto> {
    return this.getHbm<BaseBathroomHbmConfigurationDto>(this.apiUrl + 'Tunnel/hbm/' + hbmUUID);
  }

  private getInstallationData(input: GetInstallationDataInput): Promise<BathroomHbmInstallationDto> {
    return this.postHbm<{ houseMoreThan2YearsOld: boolean }, BathroomHbmInstallationDto>(
      this.apiUrl + 'Tunnel/installation/' + input.tunnelConfigurationId,
      undefined,
      { houseMoreThan2YearsOld: input.houseMoreThan2YearsOld }
    );
  }

  private getPricing(input: GetPricingInput): Promise<BathroomHbmTunnelPricingDto> {
    return this.postHbm<GetPricingInput, BathroomHbmTunnelPricingDto>(
      this.apiUrl + 'Tunnel/pricing/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  private applyDiscount(input: ApplyDiscountInput): Promise<BathroomHbmTunnelPricingDto> {
    return this.postHbm<ApplyDiscountInput, BathroomHbmTunnelPricingDto>(
      this.apiUrl + 'Tunnel/discount/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  private getFundingData(input: GetFundingDataInput): Promise<BathroomHbmTunnelPricingDto> {
    return this.postHbm<GetFundingDataInput, BathroomHbmTunnelPricingDto>(
      this.apiUrl + 'Tunnel/financing/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  private getFundingLegalInformation(input: GetFundingLegalInformationInput): Promise<FundingLegalInformation> {
    return this.getHbm<FundingLegalInformation>(
      this.apiUrl + 'Tunnel/financing/legal-information/' + input.tunnelConfigurationId,
      input
    );
  }

  private updateInstallation(input: UpdateInstallationInput): Promise<void> {
    return this.putHbm<TunnelUpdateInstallation, void>(
      this.apiUrl + 'Tunnel/installation/' + input.tunnelConfigurationId,
      input.installationUpdate
    );
  }

  /** Génère le récapitulatif PDF du projet SDB 3D (parcours ByMe/HBM). */
  public async generateBathroomHbmPdf(input: HbmPdfViewModel) {
    return await this.getBathroomHbmPdf(input);
  }

  private getBathroomHbmPdf(input: HbmPdfViewModel) {
    return this.postHbm<HbmPdfViewModel, any>(this.baseApiUrl + 'Pdf/GeneratePdf/bathroom-hbm/', input, {
      tunnelConfigurationId: input.tunnelConfigurationId,
    });
  }

  // #endregion
}
